import { 
  doc, 
  getDoc, 
  updateDoc, 
  Timestamp,
  collection, 
  query, 
  where, 
  getDocs, 
  setDoc 
} from 'firebase/firestore';
import { db } from './config';

/**
 * Check if customer is eligible for loyalty discount
 * A customer is loyal if they have made 1 or more previous purchases
 */
export const checkLoyaltyEligibility = async (customerName) => {
  try {
    if (!customerName || typeof customerName !== 'string') {
      return {
        success: false,
        isLoyal: false,
        error: 'Invalid customer name'
      };
    }

    // Find customer by name — case-insensitive by checking both exact and lowercase
    const customersRef = collection(db, 'customers');
    const q = query(customersRef, where('name', '==', customerName));
    let snapshot = await getDocs(q);

    // If no exact match, try case-insensitive by fetching all and filtering
    if (snapshot.empty) {
      const allSnap = await getDocs(collection(db, 'customers'));
      const match = allSnap.docs.find(
        d => (d.data().name || '').toLowerCase() === customerName.toLowerCase()
      );
      if (match) {
        snapshot = { empty: false, docs: [match] };
      }
    }

    if (snapshot.empty) {
      return {
        success: true,
        isLoyal: false,
        isNewCustomer: true,
        totalOrders: 0,
        totalSpent: 0
      };
    }

    const customerDoc = snapshot.docs[0];
    const customerData = customerDoc.data();
    const totalOrders = customerData.totalOrders || 0;
    const totalSpent = customerData.totalSpent || 0;

    // Customer is loyal if they have 1 or more previous orders
    const isLoyal = totalOrders >= 1;

    return {
      success: true,
      isLoyal,
      isNewCustomer: false,
      customerId: customerDoc.id,
      customerData: {
        name: customerData.name,
        totalOrders,
        totalSpent,
        phone: customerData.phone || '',
        email: customerData.email || '',
        lastPurchaseDate: customerData.lastPurchaseDate || null
      }
    };
  } catch (error) {
    console.error('Error checking loyalty eligibility:', error);
    return {
      success: false,
      isLoyal: false,
      error: error.message
    };
  }
};

/**
 * Get loyalty discount settings from billing settings
 */
export const getLoyaltyDiscountSettings = async () => {
  try {
    const settingsDoc = await getDoc(doc(db, 'settings', 'billing'));
    
    if (!settingsDoc.exists()) {
      // Default loyalty discount settings
      return {
        success: true,
        enabled: true,
        discountType: 'percentage', // 'percentage' or 'fixed'
        discountValue: 5, // 5% default
        minOrdersRequired: 1, // Minimum orders to qualify
        message: 'Loyalty Discount Applied! Thank you for being a valued customer.'
      };
    }

    const settings = settingsDoc.data();
    const loyaltySettings = settings.loyaltyDiscount || {
      enabled: true,
      discountType: 'percentage',
      discountValue: 5,
      minOrdersRequired: 1,
      message: 'Loyalty Discount Applied! Thank you for being a valued customer.'
    };

    return {
      success: true,
      ...loyaltySettings
    };
  } catch (error) {
    console.error('Error getting loyalty discount settings:', error);
    return {
      success: false,
      error: error.message,
      enabled: false
    };
  }
};

/**
 * Calculate loyalty discount amount
 */
export const calculateLoyaltyDiscount = (subtotal, discountType, discountValue) => {
  if (!subtotal || subtotal <= 0) return 0;
  
  if (discountType === 'percentage') {
    return (subtotal * discountValue) / 100;
  } else if (discountType === 'fixed') {
    return Math.min(discountValue, subtotal); // Don't exceed subtotal
  }
  
  return 0;
};

/**
 * Update loyalty discount settings
 */
export const updateLoyaltyDiscountSettings = async (settings) => {
  try {
    const settingsRef = doc(db, 'settings', 'billing');
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      // Create settings document if it doesn't exist
      await setDoc(settingsRef, {
        loyaltyDiscount: settings,
        updatedAt: Timestamp.now()
      });
    } else {
      // Update existing settings
      await updateDoc(settingsRef, {
        loyaltyDiscount: settings,
        updatedAt: Timestamp.now()
      });
    }

    return {
      success: true,
      message: 'Loyalty discount settings updated successfully'
    };
  } catch (error) {
    console.error('Error updating loyalty discount settings:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get loyalty tier settings from billing settings
 */
export const getLoyaltyTierSettings = async () => {
  try {
    const settingsDoc = await getDoc(doc(db, 'settings', 'billing'));
    
    if (!settingsDoc.exists() || !settingsDoc.data().loyaltyDiscount?.tiers) {
      // Default loyalty tier settings
      return {
        success: true,
        tiers: [
          {
            name: 'Bronze',
            icon: '🥉',
            color: '#CD7F32',
            minOrders: 1,
            maxOrders: 9,
            bonusDiscount: 0
          },
          {
            name: 'Silver',
            icon: '🥈',
            color: '#C0C0C0',
            minOrders: 10,
            maxOrders: 19,
            bonusDiscount: 1
          },
          {
            name: 'Gold',
            icon: '🥇',
            color: '#FFD700',
            minOrders: 20,
            maxOrders: 49,
            bonusDiscount: 2
          },
          {
            name: 'Platinum',
            icon: '💎',
            color: '#E5E4E2',
            minOrders: 50,
            maxOrders: 999999,
            bonusDiscount: 3
          }
        ]
      };
    }

    const tierData = settingsDoc.data().loyaltyDiscount.tiers;
    
    // Convert from object format to array format
    const tiers = [
      {
        name: 'Bronze',
        icon: '🥉',
        color: '#CD7F32',
        minOrders: tierData.bronze?.minOrders || 1,
        maxOrders: tierData.bronze?.maxOrders || 9,
        bonusDiscount: tierData.bronze?.bonusDiscount || 0
      },
      {
        name: 'Silver',
        icon: '🥈',
        color: '#C0C0C0',
        minOrders: tierData.silver?.minOrders || 10,
        maxOrders: tierData.silver?.maxOrders || 19,
        bonusDiscount: tierData.silver?.bonusDiscount || 1
      },
      {
        name: 'Gold',
        icon: '🥇',
        color: '#FFD700',
        minOrders: tierData.gold?.minOrders || 20,
        maxOrders: tierData.gold?.maxOrders || 49,
        bonusDiscount: tierData.gold?.bonusDiscount || 2
      },
      {
        name: 'Platinum',
        icon: '💎',
        color: '#E5E4E2',
        minOrders: tierData.platinum?.minOrders || 50,
        maxOrders: tierData.platinum?.maxOrders || 999999,
        bonusDiscount: tierData.platinum?.bonusDiscount || 3
      }
    ];

    return {
      success: true,
      tiers
    };
  } catch (error) {
    console.error('Error getting loyalty tier settings:', error);
    return {
      success: false,
      error: error.message,
      tiers: []
    };
  }
};

/**
 * Update loyalty tier settings
 */
export const updateLoyaltyTierSettings = async (tiers) => {
  try {
    const settingsRef = doc(db, 'settings', 'billing');
    const settingsDoc = await getDoc(settingsRef);
    
    if (!settingsDoc.exists()) {
      await setDoc(settingsRef, {
        loyaltyTiers: tiers,
        updatedAt: Timestamp.now()
      });
    } else {
      await updateDoc(settingsRef, {
        loyaltyTiers: tiers,
        updatedAt: Timestamp.now()
      });
    }

    return {
      success: true,
      message: 'Loyalty tier settings updated successfully'
    };
  } catch (error) {
    console.error('Error updating loyalty tier settings:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get customer loyalty tier based on total orders
 */
export const getCustomerLoyaltyTier = async (totalOrders) => {
  try {
    const tierSettings = await getLoyaltyTierSettings();
    
    if (!tierSettings.success || !tierSettings.tiers || tierSettings.tiers.length === 0) {
      // Fallback to default tiers
      if (totalOrders >= 50) {
        return {
          tier: 'Platinum',
          color: '#E5E4E2',
          icon: '💎',
          bonusDiscount: 3
        };
      } else if (totalOrders >= 20) {
        return {
          tier: 'Gold',
          color: '#FFD700',
          icon: '🥇',
          bonusDiscount: 2
        };
      } else if (totalOrders >= 10) {
        return {
          tier: 'Silver',
          color: '#C0C0C0',
          icon: '🥈',
          bonusDiscount: 1
        };
      } else if (totalOrders >= 1) {
        return {
          tier: 'Bronze',
          color: '#CD7F32',
          icon: '🥉',
          bonusDiscount: 0
        };
      } else {
        return {
          tier: 'New Customer',
          color: '#9E9E9E',
          icon: '👤',
          bonusDiscount: 0
        };
      }
    }

    // Find matching tier based on order count
    for (const tierConfig of tierSettings.tiers) {
      if (totalOrders >= tierConfig.minOrders && totalOrders <= tierConfig.maxOrders) {
        return {
          tier: tierConfig.name,
          color: tierConfig.color,
          icon: tierConfig.icon,
          bonusDiscount: tierConfig.bonusDiscount
        };
      }
    }

    // If no tier matches, return new customer
    return {
      tier: 'New Customer',
      color: '#9E9E9E',
      icon: '👤',
      bonusDiscount: 0
    };
  } catch (error) {
    console.error('Error getting customer loyalty tier:', error);
    // Fallback to default
    if (totalOrders >= 1) {
      return {
        tier: 'Bronze',
        color: '#CD7F32',
        icon: '🥉',
        bonusDiscount: 0
      };
    }
    return {
      tier: 'New Customer',
      color: '#9E9E9E',
      icon: '👤',
      bonusDiscount: 0
    };
  }
};
